import { isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'

type UpdateFunction<T extends z.ZodType<Record<string, unknown>>> = Partial<z.infer<T>> | ((prev: z.infer<T>) => Partial<z.infer<T>>)

type UseUrlStateReturnType<T extends z.ZodType<Record<string, unknown>>> = {
    urlState: z.infer<T>
    setUrlState: (update: UpdateFunction<T>) => void
    resetUrlState: () => void
}

export function useUrlState<T extends z.ZodType<Record<string, unknown>>>(schema: T, key: string): UseUrlStateReturnType<T> {
    const [searchParams, setSearchParams] = useSearchParams()

    const defaultState = useMemo(() => schema.parse({}) as z.infer<T>, [schema])

    const parseFromUrl = useCallback((): z.infer<T> => {
        const raw = searchParams.get('q')
        if (!raw) {
            return defaultState
        }
        try {
            const decoded = decodeURIComponent(raw)
            const data = JSON.parse(decoded)
            const parsed = schema.safeParse(data)

            if (!parsed.success) {
                return defaultState
            }
            return parsed.data
        } catch {
            return defaultState
        }
    }, [searchParams, defaultState, schema])

    const [urlState, setUrlStateInternal] = useState<z.infer<T>>(parseFromUrl)
    const query = searchParams.get('q')
    const lastQuery = useRef(query)

    useEffect(() => {
        if (query !== lastQuery.current) {
            lastQuery.current = query
            setUrlStateInternal(parseFromUrl())
            if (query) {
                sessionStorage.setItem(key, query)
            } else {
                sessionStorage.removeItem(key)
            }
        }
    }, [query, parseFromUrl, key])

    const setUrlState = useCallback(
        (update: UpdateFunction<T>) => {
            setUrlStateInternal((prev) => {
                const partialState = typeof update === 'function' ? update(prev) : update
                const newState = { ...prev, ...partialState } as z.infer<T>

                const newSearchParams = new URLSearchParams(searchParams)

                if (isEqual(newState, defaultState)) {
                    newSearchParams.delete('q')
                    sessionStorage.removeItem(key)
                } else {
                    const encoded = encodeURIComponent(JSON.stringify(newState))
                    newSearchParams.set('q', encoded)
                    sessionStorage.setItem(key, encoded)
                }

                // Keep local values (including Dates) intact when this hook updates the URL itself.
                lastQuery.current = newSearchParams.get('q')
                setSearchParams(newSearchParams, { replace: true })
                return newState
            })
        },
        [searchParams, setSearchParams, defaultState, key]
    )

    const resetUrlState = useCallback(() => {
        setUrlStateInternal(defaultState)
        const emptySearchParams = new URLSearchParams()
        lastQuery.current = null
        setSearchParams(emptySearchParams, { replace: true })
        sessionStorage.removeItem(key)
    }, [defaultState, setSearchParams, key])

    return { urlState, setUrlState, resetUrlState }
}

export const getLatestUrlParams = (key: string): string | undefined => {
    const item = sessionStorage.getItem(key)
    return item ? `q=${item}` : ''
}
